import React, { useCallback, useMemo, useState } from 'react';

import { Col, Container, Row, Button } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useTimer } from 'use-timer';
import { useDispatch } from 'react-redux';
import { useUnmount, useUpdateEffect, usePrevious } from 'react-use';
import { DateTime } from 'luxon';
import { isEqual } from 'lodash';

import { FixedFooter } from 'components/UI';
import { play as playIcon, pause as pauseIcon } from 'assets';
import { useAxios } from 'configs/requests';
import { formatDropdownOptions } from 'utils/Object';
import { clear, getAlerts } from 'store/clinicalLogger/reducer';
import { getCurrentGroup, getGroupByGid, getGroupTZ, getTimeLogTypes } from 'store/groups/reducer';
import { toHHMMSS } from 'utils/Time';
import { removeDuplicatedAlerts } from 'utils/Helpers/Alerts';
import { TimelogTypes, TimerStatus } from 'constants/enums';
import { isPatientCCMOnly } from 'utils/carePrograms';
import { filterTypesOptions, UNDEFINED_KEY } from 'utils/timeLogging';
import {
  DefaultBillingDate,
  getBillingPeriodEnd,
  getBillingPeriodStart,
  getDayInPeriod
} from 'utils/Date';
import { getPatientData } from 'store/patient/reducer';
import { useAppSelector } from 'hooks';
import { getUserTimeLogType } from 'store/settings/reducer';
import { useEpic } from 'contexts/EpicContext';

import ClinicalLoggerModal from './ClinicalLoggerModal';

interface IClinicalLogTimerProps {
  auto?: boolean;
  hideTimer?: boolean;
  onPost: (param?: boolean) => void;
  disabled?: boolean;
}

const ClinicalLogTimer = ({ auto, hideTimer, onPost, disabled }: IClinicalLogTimerProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { subscribe } = useEpic();
  const patientData = useAppSelector(getPatientData);

  const group = useAppSelector((state) => {
    return hideTimer
      ? getCurrentGroup(state)
      : getGroupByGid(state, patientData?.subgroup_gid || '');
  });
  const defaultClinicalTimeLogTypeId = useAppSelector((state) => getUserTimeLogType(state));
  const automaticClinicalTimeLog = useAppSelector(
    (state) => state.settings.automaticClinicalTimeLog
  );
  const allAlerts = useAppSelector(getAlerts, isEqual);
  const tasks = useAppSelector((state) => state.clinicalLogger.tasks);
  const fillSurvey = useAppSelector(({ patient }) => patient?.inProgress.survey);
  const groupTimezone = useAppSelector(getGroupTZ);
  const timeLogTypes = useAppSelector((state) => getTimeLogTypes(state));

  // TODO improvement: save time in store and restore timer after mount if it was a manual post
  // const manualPost = useAppSelector((state) => state.clinicalLogger.manualPost);
  const alerts = removeDuplicatedAlerts(allAlerts);
  const timeLoggingDisabled = group ? group.timeLogsDisabled : true;
  const hasMonitoringProgram = patientData?.patientCareProgram.some(
    (program) => program.type === 'monitoring'
  );
  const isCCMOnlyPatient = isPatientCCMOnly(patientData?.patientCareProgram || []);
  const filteredTimeLogTypes = filterTypesOptions(timeLogTypes || [], isCCMOnlyPatient);

  const billingDateStart = getBillingPeriodStart(
    patientData?.billingStartDate,
    group?.defaultBillingDate,
    groupTimezone,
    intl.locale
  );
  const billingDateEnd = getBillingPeriodEnd(
    patientData?.billingStartDate,
    group?.defaultBillingDate,
    groupTimezone,
    intl.locale
  );
  const dayInPeriod = getDayInPeriod(
    patientData?.billingStartDate,
    group?.defaultBillingDate,
    groupTimezone
  );
  const isEmbedded = useAppSelector((state) => state.settings.embedded);
  const navigationHidden = useAppSelector((state) => state.settings.hideNavigation);

  const showTimer = !hideTimer && !timeLoggingDisabled;

  const autostart = !hideTimer && auto && automaticClinicalTimeLog;
  const { time, start, pause, reset, status } = useTimer({ autostart });
  const timerRunning = status === TimerStatus.RUNNING;

  const [modal, setModal] = useState(false);

  const typesOptions = useMemo(() => {
    return filteredTimeLogTypes
      ? formatDropdownOptions(filteredTimeLogTypes, 'timeLogTypeId', 'labelKey').map((task) => {
          return {
            ...task,
            label: intl.formatMessage({
              id: task.label
            })
          };
        })
      : [];
  }, [filteredTimeLogTypes, intl]);

  const oldPatientData = usePrevious(patientData);
  const oldGroup = usePrevious(group);

  const [{ data: postData }, post] = useAxios({ method: 'POST' }, { manual: true });

  const toggleTimer = () => {
    status === TimerStatus.RUNNING ? pause() : start();
  };

  const handlePost = (param?: boolean) => {
    // Manually close the Modal and reset the timer
    // The redirection does not close the modal when it is on a page where the modal is in the react tree
    if (isEmbedded && navigationHidden) {
      setModal(false);
      reset();
      autostart && start();
    }
    onPost(param);
  };

  const saveTimeLog = useCallback(
    (patient: IPatient, group: IGroupInfo, timeSpent: number) => {
      if (!hideTimer && !group.timeLogsDisabled && !modal && autostart && time > 0) {
        const data = {
          close_alerts: [],
          uid: patient.uid,
          gid: group.gid,
          time_log_type_id:
            typesOptions?.[
              typesOptions.findIndex((type) => type.value === defaultClinicalTimeLogTypeId)
            ]?.value ||
            (isCCMOnlyPatient && TimelogTypes.CCM) ||
            null,
          log_date: DateTime.now().toUTC().toFormat('yyyy-MM-dd HH:mm:ss'),
          time_spent: timeSpent
        };
        return post({ url: '/clinician/ClinicalTimeLogs', data });
      }
    },
    [
      autostart,
      hideTimer,
      isCCMOnlyPatient,
      modal,
      post,
      time,
      typesOptions,
      defaultClinicalTimeLogTypeId
    ]
  );

  const saveOnExit = useCallback(async () => {
    patientData && group && (await saveTimeLog(patientData, group, time));
  }, [patientData, group, saveTimeLog, time]);

  const CalendarDateBillingDaysSection = () => {
    return (
      <div className="d-flex flex-row align-items-center ms-4 white-space-nowrap">
        <span className="me-2">{intl.formatMessage({ id: 'totalTimeLogHeaderKey' })}</span>
        {hasMonitoringProgram && (
          <div className="bg-light p-2 me-3">
            <span>
              {intl.formatMessage({
                id: 'TxDaysKey'
              })}
              :{' '}
            </span>
            <span data-test="clinical-timer-tx-days" className="font-weight-bold">
              {patientData?.medicalTransmissionDaysWithObservations}
            </span>
          </div>
        )}
      </div>
    );
  };

  const RollingBillingDaysSection = () => {
    return (
      <div className="d-flex flex-row align-items-center mx-2 bg-light p-2 white-space-nowrap">
        {hasMonitoringProgram && (
          <div className="me-3">
            <span>
              {intl.formatMessage({
                id: 'TxDaysKey'
              })}
              :{' '}
            </span>
            <span data-test="clinical-timer-tx-days" className="font-weight-bold">
              {patientData?.medicalTransmissionDaysWithObservations}
            </span>
          </div>
        )}
        {patientData?.billingStartDate !== undefined && (
          <>
            <p
              className="m-0 me-3 font-weight-bolder"
              data-test="patient-billing-period"
            >{`${billingDateStart} - ${billingDateEnd}`}</p>
            <p className="m-0" data-test="patient-day-in-period">
              {`${intl.formatMessage({
                id: 'dayKey'
              })} ${dayInPeriod}`}
            </p>
          </>
        )}
      </div>
    );
  };

  const getDaysSection = () => {
    if (!patientData) return null;
    switch (group?.defaultBillingDate) {
      case DefaultBillingDate.CALENDAR_DATE:
        return <CalendarDateBillingDaysSection />;
      default:
        return <RollingBillingDaysSection />;
    }
  };

  const daysSection = getDaysSection();

  useUpdateEffect(() => {
    if (postData) {
      onPost(true);
      dispatch(clear());
      reset();
      autostart && start();
    }
  }, [postData]);

  useUpdateEffect(() => {
    subscribe({
      eventId: 'Epic.Common.RequestToCloseApp',
      callback: saveOnExit,
      id: 'ClinicalLogTimer.onCloseSave'
    });
  }, [saveOnExit, subscribe]);

  useUpdateEffect(() => {
    if (oldPatientData && oldGroup) {
      saveTimeLog(oldPatientData, oldGroup, time);
      if (oldGroup.timeLogsDisabled && !timeLoggingDisabled) {
        // ensure the timer restarts when switching from a patient
        // from a group where the time logging option is disabled
        reset();
        start();
      }
    }
    patientData && !group?.timeLogsDisabled && autostart && start();
  }, [patientData?.uid]);

  useUnmount(() => {
    patientData && group && saveTimeLog(patientData, group, time);
  });

  if (alerts.length || tasks.length || !hideTimer) {
    return (
      <>
        <FixedFooter>
          <Container fluid>
            <Row>
              <Col>
                <div
                  className={`d-flex justify-content-${patientData ? 'between' : 'end'} overflow-scroll`}
                >
                  {daysSection}
                  <div className="d-flex justify-content-end align-items-center">
                    {(!timeLoggingDisabled || hasMonitoringProgram) && patientData && (
                      <div
                        className="d-flex justify-content-end align-items-center"
                        data-test="show-timer-enabled-section"
                      >
                        {!timeLoggingDisabled && (
                          <div
                            className="p-2 bg-light me-3 white-space-nowrap"
                            data-test="totalTimes"
                          >
                            {patientData?.logTypes && (
                              <>
                                {Object.entries(patientData?.logTypes)
                                  .filter(([key]) => key !== UNDEFINED_KEY)
                                  .map(([key, value], i) => {
                                    if (value > 0) {
                                      return (
                                        <span key={i}>
                                          {`${i !== 0 ? ' | ' : ''} ${intl.formatMessage({
                                            id: key
                                          })}: `}
                                          <span className="font-weight-bold">
                                            {toHHMMSS(value)}
                                          </span>
                                        </span>
                                      );
                                    }
                                  })}
                              </>
                            )}
                            {!patientData?.logTypes &&
                              patientData &&
                              intl.formatMessage({ id: 'noTimeLoggedKey' })}
                          </div>
                        )}
                      </div>
                    )}
                    {showTimer && (
                      <div className="timer" data-test="timer">
                        <h5>{toHHMMSS(time)}</h5>
                        <div className="trigger" onClick={toggleTimer}>
                          <img data-test="imgTimer" src={timerRunning ? pauseIcon : playIcon} />
                          <p>
                            {intl.formatMessage({
                              id: timerRunning ? 'pauseTimerKey' : 'playTimerKey'
                            })}
                          </p>
                        </div>
                      </div>
                    )}
                    <Button
                      data-test="close-and-document-btn"
                      className="me-4 white-space-nowrap text-overflow-ellipsis overflow-hidden min-w-200"
                      onClick={() => setModal(true)}
                      disabled={!!fillSurvey || disabled}
                    >
                      {intl.formatMessage({ id: 'closeAndDocumentKey' })}
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </FixedFooter>
        {modal && (
          <ClinicalLoggerModal
            hideTimer={hideTimer}
            onPost={handlePost}
            time={time}
            close={() => setModal(false)}
            isPaused={status === TimerStatus.PAUSED}
          />
        )}
      </>
    );
  } else {
    return <></>;
  }
};

export default ClinicalLogTimer;
